export interface RecentLocationEntry {
  idLocation: string
  lastUsedAt: number
}

const COOKIE_NAME = 'ovatu.recent_locations'
const MAX_RECENT_LOCATIONS = 5
const MAX_AGE_SECONDS = 60 * 60 * 24 * 180

export function useRecentLocations() {
  const config = useRuntimeConfig()
  const requestUrl = useRequestURL()
  const configuredDomain = String(config.public.domain || '')
  const cookieDomain = configuredDomain
    && (requestUrl.hostname === configuredDomain || requestUrl.hostname.endsWith(`.${configuredDomain}`))
    ? `.${configuredDomain}`
    : undefined
  const cookie = useCookie<RecentLocationEntry[] | null>(COOKIE_NAME, {
    default: () => [],
    domain: cookieDomain,
    path: '/',
    maxAge: MAX_AGE_SECONDS,
    sameSite: 'lax',
    secure: requestUrl.protocol === 'https:',
  })

  function read(): RecentLocationEntry[] {
    if (!Array.isArray(cookie.value)) return []

    return cookie.value
      .filter((entry): entry is RecentLocationEntry => {
        return typeof entry?.idLocation === 'string'
          && /^\d+$/.test(entry.idLocation)
          && Number.isFinite(entry?.lastUsedAt)
      })
      .map(entry => ({
        idLocation: entry.idLocation,
        lastUsedAt: entry.lastUsedAt,
      }))
      .slice(0, MAX_RECENT_LOCATIONS)
  }

  function remember(idLocation: string | number): void {
    const id = String(idLocation)
    if (!/^\d+$/.test(id)) return

    const entries = read().filter(entry => entry.idLocation !== id)
    cookie.value = [
      { idLocation: id, lastUsedAt: Date.now() },
      ...entries,
    ].slice(0, MAX_RECENT_LOCATIONS)
  }

  return {
    read,
    remember,
  }
}
